import { useState } from "react";

import type { NotePreview } from "@/lib/api";
import { useFileManagerStore } from "@/state/fileManagerStore";

import { ThumbPlaceholder } from "./ThumbImage";

interface ThumbnailWithPinsProps {
  thumbUrl: string | null;
  widthMm: number | null;
  heightMm: number | null;
  notes: NotePreview[];
}

const PIN_RADIUS_MM = 2.5;
const PREVIEW_MAX_HEIGHT_PX = 250;

export function ThumbnailWithPins({
  thumbUrl,
  widthMm,
  heightMm,
  notes,
}: ThumbnailWithPinsProps) {
  const selectedId = useFileManagerStore((state) => state.selectedNoteId);
  const setSelected = useFileManagerStore((state) => state.setSelectedNoteId);
  // F-GC.3: a rota responde 404 quando não há miniatura durável nem pirâmide no
  // disco (o card não refaz render). A mesma URL não é pedida de novo.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (!thumbUrl || failedUrl === thumbUrl) {
    return (
      <div className="flex justify-center">
        <div
          className="flex aspect-[3/4] max-h-[250px] items-center justify-center rounded border border-ink-700 bg-ink-900 text-xs text-ink-500"
          style={{ width: `min(100%, ${(PREVIEW_MAX_HEIGHT_PX * 3) / 4}px)` }}
        >
          {thumbUrl ? <ThumbPlaceholder /> : "Sem preview disponível"}
        </div>
      </div>
    );
  }

  const width = widthMm ?? 210;
  const height = heightMm ?? 297;

  return (
    <div className="flex justify-center">
      <div
        className="relative max-h-[250px] overflow-hidden rounded border border-ink-700 bg-ink-900"
        style={{
          aspectRatio: `${width} / ${height}`,
          width: `min(100%, ${PREVIEW_MAX_HEIGHT_PX * (width / height)}px)`,
        }}
      >
        <img
          src={thumbUrl}
          alt="thumbnail"
          className="h-full w-full object-contain"
          onError={() => setFailedUrl(thumbUrl)}
        />
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {notes.map((note) => {
            const active = note.id === selectedId;

            return (
              <g
                key={note.id}
                role="button"
                tabIndex={0}
                aria-label={`Selecionar nota da página ${note.page}`}
                className="pointer-events-auto cursor-pointer"
                onClick={() => setSelected(note.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelected(note.id);
                  }
                }}
              >
                <circle
                  cx={note.x_mm}
                  cy={note.y_mm}
                  r={active ? PIN_RADIUS_MM * 1.5 : PIN_RADIUS_MM}
                  fill={active ? "#22c55e" : "#facc15"}
                  stroke="#1f2937"
                  strokeWidth={0.5}
                />
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
